import React from 'react';
import { cloneDeep } from 'lodash';

import { getClass } from '../../../utils/component-helpers';
import MandatoryFormItem from './form-mandatory-item';
import CategoriesForm from './form-categories';
import BasicDropdown from '../../base/dropdowns/basic-dropdown';
import Flag from '../../base/flag/flag';
import RadioButtons from './radio-btn';

import { titleCase } from '../../base/common/utils';

import './styles/form-acccount-details.scss';

const publisher = 'publisher';
const merchant = 'merchant';

const AccountDetails = (
  account,
  accountType,
  pubType,
  pubTypes,
  setPubType,
  fieldErrors,
  slug,
  setSlug,
  editableSlug,
  selectedFile,
  fullName,
  setFullName,
  onFileUpload,
  shortName,
  setShortName,
  categories,
  setCategories,
  url,
  setUrl,
  uploadButton,
  handleClickUpload,
  editing,
  pilot,
  setPilot,
  showInDashboard,
  setShowInDashboard,
  isActive,
  setIsActive,
  merchBillingOption,
  setMerchBillingOption,
  campaignInvoicing,
  setCampaignInvoicing,
  flags,
) => {
  const requiredFields = [
    { label: 'Company', type: 'full_name', value: fullName, setter: setFullName },
    { label: 'Short Name', type: 'short_name', value: shortName, setter: setShortName },
    {
      label: 'Category',
      type: 'categories',
      value: categories,
      setter: addCategory,
      removeCategory,
    },
    { label: 'Url', type: 'url', value: url, setter: setUrl },
    ...(editableSlug ? [{ label: 'Slug', type: 'slug', value: slug, setter: setSlug }] : []),
  ];

  const isActiveOptions = [
    { label: 'Active', value: true, selected: !!isActive },
    { label: 'Inactive', value: false, selected: !isActive },
  ];

  const billingOptions = [
    {
      label: 'Automated',
      value: true,
      text: `This merchant will participate in streamlined invoicing of marketplace activity and automated
				payments to publishers. Please note this will not include
				any other kinds of invoicing (e.g., Subscriptions)`,
    },
    {
      label: 'Manual',
      value: false,
      text: 'This merchant will be billed manually via email.',
    },
  ];

  const invoiceGroupingOptions = [
    {
      label: 'Aggregate Invoicing',
      value: false,
      text: `Monthly invoice will be grouped by merchant's total monthly spend`,
    },
    {
      label: 'Campaign Invoicing',
      value: true,
      text: `Monthly invoice will be grouped by merchant's monthly spend per campaign`,
    },
  ];

  function addCategory(category) {
    const newCategories = cloneDeep(categories);
    newCategories.push(category);
    setCategories(newCategories);
  }

  function removeCategory(category) {
    const newCategories = cloneDeep(categories).filter(newCategory => newCategory !== category);
    setCategories(newCategories);
  }

  const pubTypeItem =
    accountType === publisher ? (
      <div className="form-item">
        <label>
          Type <span className="star">*</span>
        </label>
        <div className="pub-type">
          <BasicDropdown
            items={pubTypes}
            selectItem={idx => setPubType(pubTypes[idx])}
            dropdownLabel={pubType.label}
          />
        </div>
      </div>
    ) : (
      <div className="form-item">
        <label>
          Type <span className="star"></span>
        </label>
        <div className="pub-type">{titleCase(merchant)}</div>
      </div>
    );

  const logoPreview =
    selectedFile || account.logo_url ? (
      <div className="image-button">
        <div className="logo-preview">
          <img
            src={selectedFile ? URL.createObjectURL(selectedFile) : account.logo_url}
            alt="logo"
          />
        </div>
      </div>
    ) : null;

  const toggleBtns = isActiveOptions.map(({ label, value, selected }, idx) => (
    <div
      key={idx}
      onClick={() => setIsActive(value)}
      className={getClass('toggle-item', {
        left: !idx,
        right: idx,
        selected,
      })}
    >
      {label}
    </div>
  ));

  const merchantActiveToggle =
    accountType === merchant ? (
      <div className="form-item">
        <div className="state">
          <label>State</label>
        </div>
        <div className="toggle-area">
          <div className="toggle-btns">{toggleBtns}</div>
        </div>
      </div>
    ) : null;

  const campaignInvoicingRadioBtns = merchBillingOption ? (
    <RadioButtons
      label="Invoice Grouping"
      options={invoiceGroupingOptions}
      selectedOption={campaignInvoicing}
      setOption={setCampaignInvoicing}
    />
  ) : null;

  const merchantBilling =
    accountType === merchant ? (
      <Flag name="adminBillingFeatures">
        <RadioButtons
          label="Billing"
          options={billingOptions}
          selectedOption={merchBillingOption}
          setOption={setMerchBillingOption}
        />
        {campaignInvoicingRadioBtns}
      </Flag>
    ) : null;

  const requiredFieldsMap = requiredFields.map(
    ({ label, type, value, setter, removeCategory }, idx) =>
      type !== 'categories' ? (
        <MandatoryFormItem
          key={idx}
          label={label}
          type={type}
          value={value}
          setter={setter}
          fieldErrors={fieldErrors}
        />
      ) : flags.accountCategorization ? (
        <CategoriesForm
          key={idx}
          label={label}
          type={type}
          value={value}
          setter={setter}
          fieldErrors={fieldErrors}
          removeCategory={removeCategory}
          chosenCategories={categories}
        />
      ) : null,
  );

  const autosavedSlug = !editableSlug ? (
    <div className="form-item">
      <label>Slug</label>
      <div className="prev-slug">{slug}</div>
    </div>
  ) : null;

  return (
    <div className="form">
      {pubTypeItem}
      {merchantActiveToggle}
      {requiredFieldsMap.slice(0, 1)}
      <div className="form-item">
        <label></label>
        <div className="display-inputs">
          <label className="checkbox-label" onClick={() => setPilot(!pilot)}>
            <span className={`pilot checkbox ${pilot ? 'selected' : ''}`}></span>
            Is Pilot?
          </label>
          <label className="checkbox-label" onClick={() => setShowInDashboard(!showInDashboard)}>
            <span
              className={`show-in-dashboard checkbox ${showInDashboard ? 'selected' : ''}`}
            ></span>
            Show In Dashboard
          </label>
        </div>
      </div>
      {requiredFieldsMap.slice(1)}
      {autosavedSlug}
      {merchantBilling}
      <div className="form-item">
        <label>Logo</label>
        <div className="upload-controls">
          {logoPreview}
          <input
            className="file-btn"
            name="file"
            type="file"
            id="file"
            accept="image/*"
            onChange={onFileUpload}
            ref={node => (uploadButton.current = node)}
            style={{ display: 'none' }}
          />
          <button className="upload-btn" onClick={handleClickUpload}>
            {!editing ? 'Upload' : 'Edit'} Logo
          </button>
          <span className="filename">{selectedFile && selectedFile.name}</span>
        </div>
      </div>
    </div>
  );
};

export default AccountDetails;
